import React from 'react';

interface PresetFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filterData: {
    request_name: string;
    filters: {
      // New structure to match PresetFilterForm
      location?: {
        locations?: string[];
        countries?: string[];
        states?: string[];
      };
      industry?: {
        industries?: string[];
        companies?: string[];
        sizes?: string[];
      };
      position?: {
        titles?: string;
        seniority?: string[];
        skills?: string[];
        functions?: string[];
      };
      // Keep old structure for backward compatibility
      geography?: {
        locations?: string[];
        countries?: string[];
        regions?: string[];
      };
      company?: {
        industries?: string[];
        sizes?: string[];
        types?: string[];
        revenue?: string[];
      };
      role?: {
        titles?: string;
        seniority?: string[];
        functions?: string[];
        experience?: string[];
      };
      personal?: {
        schools?: string[];
        skills?: string[];
        languages?: string[];
      };
      engagement?: {
        groups?: string[];
        following?: string[];
        activity?: string[];
      };
    };
    target_lead_count?: number;
    priority?: string;
    created_at?: string;
  };
  campaignName: string;
}

const PresetFilterModal: React.FC<PresetFilterModalProps> = ({
  isOpen,
  onClose,
  filterData,
  campaignName
}) => {
  if (!isOpen) return null;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Show brief success message
    const button = document.activeElement as HTMLButtonElement;
    if (button) {
      const originalText = button.textContent;
      button.textContent = 'Copied!';
      button.classList.add('bg-green-600');
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('bg-green-600');
      }, 2000);
    }
  };

  const formatFiltersForCopy = () => {
    let text = `Filter Request: ${filterData.request_name}\n`;
    text += `Campaign: ${campaignName}\n`;
    text += `Target Lead Count: ${filterData.target_lead_count || 'Not specified'}\n`;
    text += `Priority: ${filterData.priority || 'Normal'}\n\n`;
    text += `FILTER CRITERIA:\n`;
    text += `================\n\n`;

    // Check for new structure first (location, industry, position)
    if (filterData.filters.location) {
      text += `LOCATION:\n`;
      if (filterData.filters.location.locations?.length) {
        text += `• Locations: ${filterData.filters.location.locations.join(', ')}\n`;
      }
      if (filterData.filters.location.countries?.length) {
        text += `• Countries: ${filterData.filters.location.countries.join(', ')}\n`;
      }
      if (filterData.filters.location.states?.length) {
        text += `• States/Regions: ${filterData.filters.location.states.join(', ')}\n`;
      }
      text += '\n';
    } else if (filterData.filters.geography) {
      // Fallback to old structure
      text += `GEOGRAPHY:\n`;
      if (filterData.filters.geography.locations?.length) {
        text += `• Locations: ${filterData.filters.geography.locations.join(', ')}\n`;
      }
      if (filterData.filters.geography.countries?.length) {
        text += `• Countries: ${filterData.filters.geography.countries.join(', ')}\n`;
      }
      text += '\n';
    }

    if (filterData.filters.industry) {
      text += `INDUSTRY:\n`;
      if (filterData.filters.industry.industries?.length) {
        text += `• Industries: ${filterData.filters.industry.industries.join(', ')}\n`;
      }
      if (filterData.filters.industry.companies?.length) {
        text += `• Companies: ${filterData.filters.industry.companies.join(', ')}\n`;
      }
      if (filterData.filters.industry.sizes?.length) {
        text += `• Company Size: ${filterData.filters.industry.sizes.join(', ')}\n`;
      }
      text += '\n';
    } else if (filterData.filters.company) {
      // Fallback to old structure
      text += `COMPANY:\n`;
      if (filterData.filters.company.industries?.length) {
        text += `• Industries: ${filterData.filters.company.industries.join(', ')}\n`;
      }
      if (filterData.filters.company.sizes?.length) {
        text += `• Company Size: ${filterData.filters.company.sizes.join(', ')}\n`;
      }
      text += '\n';
    }

    if (filterData.filters.position) {
      text += `POSITION:\n`;
      if (filterData.filters.position.titles) {
        text += `• Job Titles: ${filterData.filters.position.titles}\n`;
      }
      if (filterData.filters.position.seniority?.length) {
        text += `• Seniority: ${filterData.filters.position.seniority.join(', ')}\n`;
      }
      if (filterData.filters.position.skills?.length) {
        text += `• Skills: ${filterData.filters.position.skills.join(', ')}\n`;
      }
      if (filterData.filters.position.functions?.length) {
        text += `• Functions: ${filterData.filters.position.functions.join(', ')}\n`;
      }
      text += '\n';
    } else if (filterData.filters.role) {
      // Fallback to old structure
      text += `ROLE:\n`;
      if (filterData.filters.role.titles) {
        text += `• Job Titles: ${filterData.filters.role.titles}\n`;
      }
      if (filterData.filters.role.seniority?.length) {
        text += `• Seniority: ${filterData.filters.role.seniority.join(', ')}\n`;
      }
      text += '\n';
    }

    // Old structure only sections (if they exist)
    if (filterData.filters.personal) {
      text += `PERSONAL:\n`;
      if (filterData.filters.personal.schools?.length) {
        text += `• Schools: ${filterData.filters.personal.schools.join(', ')}\n`;
      }
      if (filterData.filters.personal.skills?.length) {
        text += `• Skills: ${filterData.filters.personal.skills.join(', ')}\n`;
      }
      text += '\n';
    }

    if (filterData.filters.engagement) {
      text += `ENGAGEMENT:\n`;
      if (filterData.filters.engagement.groups?.length) {
        text += `• Groups: ${filterData.filters.engagement.groups.join(', ')}\n`;
      }
      if (filterData.filters.engagement.activity?.length) {
        text += `• Activity: ${filterData.filters.engagement.activity.join(', ')}\n`;
      }
    }

    return text;
  };

  const getActivityLabel = (activity: string) => {
    const labels: { [key: string]: string } = {
      'posted-30days': 'Posted in last 30 days',
      'posted-90days': 'Posted in last 90 days',
      'changed-jobs': 'Changed jobs recently'
    };
    return labels[activity] || activity;
  };

  const getPriorityBadge = (priority?: string) => {
    const priorityColors = {
      low: 'bg-gray-100 text-gray-800',
      normal: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    const color = priorityColors[priority as keyof typeof priorityColors] || priorityColors.normal;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {(priority || 'normal').toUpperCase()}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-white">
                  Target Lead Criteria for Campaign
                </h2>
                <p className="text-purple-100 text-sm mt-1">{campaignName}</p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
            {/* Request Details */}
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Request Name</p>
                  <p className="font-medium">{filterData.request_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Target Lead Count</p>
                  <p className="font-medium">{filterData.target_lead_count || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Priority</p>
                  <div className="mt-1">{getPriorityBadge(filterData.priority)}</div>
                </div>
              </div>
            </div>

            {/* Filter Sections */}
            <div className="p-6 space-y-6">
              {/* Location (new structure) */}
              {filterData.filters.location && Object.keys(filterData.filters.location).length > 0 && (
                <div>
                  <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
                    <span className="mr-2">📍</span> Location
                  </h3>
                  <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                    {filterData.filters.location.locations?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Locations:</span>
                        <div className="mt-1">
                          {filterData.filters.location.locations.map((loc, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {loc}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {filterData.filters.location.countries?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Countries:</span>
                        <div className="mt-1">
                          {filterData.filters.location.countries.map((country, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {country}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {filterData.filters.location.states?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">States/Regions:</span>
                        <div className="mt-1">
                          {filterData.filters.location.states.map((state, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {state}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Geography (old structure fallback) */}
              {!filterData.filters.location && filterData.filters.geography && Object.keys(filterData.filters.geography).length > 0 && (
                <div>
                  <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
                    <span className="mr-2">🌍</span> Geography
                  </h3>
                  <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                    {filterData.filters.geography.locations?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Locations:</span>
                        <div className="mt-1">
                          {filterData.filters.geography.locations.map((loc, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {loc}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {filterData.filters.geography.countries?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Countries:</span>
                        <div className="mt-1">
                          {filterData.filters.geography.countries.map((country, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {country}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Industry (new structure) */}
              {filterData.filters.industry && Object.keys(filterData.filters.industry).length > 0 && (
                <div>
                  <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
                    <span className="mr-2">🏢</span> Industry
                  </h3>
                  <div className="bg-green-50 rounded-lg p-4 space-y-2">
                    {filterData.filters.industry.industries?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Industries:</span>
                        <div className="mt-1">
                          {filterData.filters.industry.industries.map((industry, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {industry}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {filterData.filters.industry.companies?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Companies:</span>
                        <div className="mt-1">
                          {filterData.filters.industry.companies.map((company, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {company}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {filterData.filters.industry.sizes?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Company Size:</span>
                        <div className="mt-1">
                          {filterData.filters.industry.sizes.map((size, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {size}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Company (old structure fallback) */}
              {!filterData.filters.industry && filterData.filters.company && Object.keys(filterData.filters.company).length > 0 && (
                <div>
                  <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
                    <span className="mr-2">🏢</span> Company
                  </h3>
                  <div className="bg-green-50 rounded-lg p-4 space-y-2">
                    {filterData.filters.company.industries?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Industries:</span>
                        <div className="mt-1">
                          {filterData.filters.company.industries.map((industry, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {industry}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {filterData.filters.company.sizes?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Company Size:</span>
                        <div className="mt-1">
                          {filterData.filters.company.sizes.map((size, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {size}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Position (new structure) */}
              {filterData.filters.position && Object.keys(filterData.filters.position).length > 0 && (
                <div>
                  <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
                    <span className="mr-2">💼</span> Position
                  </h3>
                  <div className="bg-purple-50 rounded-lg p-4 space-y-2">
                    {filterData.filters.position.titles && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Job Titles:</span>
                        <p className="mt-1 text-gray-900">{filterData.filters.position.titles}</p>
                      </div>
                    )}
                    {filterData.filters.position.seniority?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Seniority Level:</span>
                        <div className="mt-1">
                          {filterData.filters.position.seniority.map((level, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {level}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {filterData.filters.position.skills?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Skills:</span>
                        <div className="mt-1">
                          {filterData.filters.position.skills.map((skill, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {filterData.filters.position.functions?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Functions:</span>
                        <div className="mt-1">
                          {filterData.filters.position.functions.map((func, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {func}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Role (old structure fallback) */}
              {!filterData.filters.position && filterData.filters.role && Object.keys(filterData.filters.role).length > 0 && (
                <div>
                  <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
                    <span className="mr-2">💼</span> Role
                  </h3>
                  <div className="bg-purple-50 rounded-lg p-4 space-y-2">
                    {filterData.filters.role.titles && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Job Titles:</span>
                        <p className="mt-1 text-gray-900">{filterData.filters.role.titles}</p>
                      </div>
                    )}
                    {filterData.filters.role.seniority?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Seniority Level:</span>
                        <div className="mt-1">
                          {filterData.filters.role.seniority.map((level, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {level}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Personal */}
              {filterData.filters.personal && Object.keys(filterData.filters.personal).length > 0 && (
                <div>
                  <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
                    <span className="mr-2">👤</span> Personal
                  </h3>
                  <div className="bg-yellow-50 rounded-lg p-4 space-y-2">
                    {filterData.filters.personal.schools?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Schools:</span>
                        <div className="mt-1">
                          {filterData.filters.personal.schools.map((school, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {school}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {filterData.filters.personal.skills?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Skills:</span>
                        <div className="mt-1">
                          {filterData.filters.personal.skills.map((skill, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Engagement */}
              {filterData.filters.engagement && Object.keys(filterData.filters.engagement).length > 0 && (
                <div>
                  <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
                    <span className="mr-2">💬</span> Engagement
                  </h3>
                  <div className="bg-orange-50 rounded-lg p-4 space-y-2">
                    {filterData.filters.engagement.groups?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">LinkedIn Groups:</span>
                        <div className="mt-1">
                          {filterData.filters.engagement.groups.map((group, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {group}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {filterData.filters.engagement.activity?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Activity Level:</span>
                        <div className="mt-1">
                          {filterData.filters.engagement.activity.map((activity, idx) => (
                            <span key={idx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">
                              {getActivityLabel(activity)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="p-6 bg-blue-50 border-t border-blue-100">
              <h3 className="font-medium text-blue-900 mb-2">📋 Instructions for CSR:</h3>
              <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
                <li>Open LinkedIn Sales Navigator</li>
                <li>Apply the filters shown above to match the criteria</li>
                <li>Export the matching leads to CSV format</li>
                <li>Upload the CSV file to the campaign dashboard</li>
                <li>Mark the campaign as "Running" once leads are imported</li>
              </ol>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between">
            <button
              onClick={() => copyToClipboard(formatFiltersForCopy())}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy Criteria
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresetFilterModal;